import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const sunlifeCaseStudy: ContentObject = {
    id: 'sunlife-genai-legacy',
    kind: 'system',
    type: 'case-study',
    name: 'Strategic GenAI Adoption Reinvents Legacy Systems at Sun Life',
    description:
        "Sun Life uses IBM's watsonX for legacy code (COBOL, PL/I) conversion and natural language extraction of business rules, coupled with AI literacy programs via CIFAR, balancing innovation with risk.",
    url: 'https://www.youtube.com/watch?v=Vj6Ocj36KJs',
    scores: createScores({
        detailedMetrics: 2.0,
        relevanceToUseCase: 7.0,
        reliabilityOfSource: 8.0,
        strategicAndTacticalInformation: 7.0,
        technicalImplementationDetails: 6.0,
        noVendorBias: 7.0,
        relevanceToClient: 10.0,
    }),
    publishedAt: new Date(Date.now() - 17 * 24 * 60 * 60 * 1000), // 17 days ago
    markdown: `# 1. Who the Source of Information Is
 
 Source Kind: YouTube 
 Interview Title: "Strategic AI Adoption: Sun Life"  
 Interviewee: Janet Weldon, AVP of Global Enablement Strategy (Digital Strategy & Enablement) at Sun Life 
 Key Background: Janet leads Sun Life's enterprise-wide generative AI (GenAI) adoption, focusing on data and AI literacy. Her role encompasses ensuring ethical practices and maintaining a balance between innovation and risk management.
 
 Relevant Quote: 
 > ""…our role at Sun Life really about is about gen adoption enterprise-wide across the organization and a big part of Enterprise gen adoption of course is data and AI literacy.""
 
 # 2. Successes
 
 Sun Life's journey shows several notable successes in integrating GenAI to boost software development and overall operational efficiency:
 
 ## Modernization Through Legacy Code Conversion: 
 Sun Life is leveraging IBM's watsonX to modernize legacy mainframe systems. This includes converting COBOL and PL/I code, which not only accelerates client initiatives but also mitigates risks associated with retiring talent. 
 Relevant Quote: 
 > ""…we've been doing some really exciting experimentation on the Mainframe modernization side of things so we've been looking at how Legacy code conversion of COBOL and PL/I can help us to accelerate some of our client initiatives and it also can help us mitigate some of that retirement risk…""
 
 ## Extracting Business Insights from Legacy Code: 
 The conversion process goes beyond simply modernizing code. It is also used to convert code into natural language, allowing for the extraction of historical business rules. This process enhances understanding of long-standing procedures and practices, effectively preserving institutional knowledge. 
 Relevant Quote: 
 > ""…it's really exciting that we can not only modernize the code itself but we can also convert it into natural language so you can start to extract some of those historical business rules and things like that.""
 
 ## Establishing Early GenAI Guardrails: 
 Sun Life formed a community of practice that includes experts from legal, risk, compliance, privacy, and HR. Involving these cross-functional teams from the very beginning helped develop solid guardrails for responsible generative AI usage. 
 Relevant Quote: 
 > ""…one of the things we did very early in our generative AI Journey was form a community of practice…that group was really instrumental in forming some guardrails around the adoption experimentation with generative AI at Sun Life.""
 
 ## Enhancing Employee Skills in AI Literacy: 
 Recognizing that human expertise remains crucial, Sun Life invested in upskilling its employees. The company partnered with the Canadian Institute for Advanced Research (CIFAR) to offer a "destination AI" certificate to all employees, ensuring a well-informed and responsible workforce. 
 Relevant Quote: 
 > ""…we've recently announced we partnered with CIFAR…we've been able to offer to all Sun Life employees globally the destination AI certificate…""
 
 # 3. Challenges
 
 While the conversation mainly emphasized successes and proactive measures, a few challenges inherent to the modernization and AI adoption process were acknowledged:
 
 ## Legacy System Risk of Knowledge Loss: 
 A central challenge mentioned is the risk of losing valuable institutional knowledge tied to legacy systems because experienced professionals or programmers may retire. The conversion of legacy systems (with languages like COBOL) is not only a technical hurdle but also a key risk mitigation strategy. 
 Relevant Quote (Challenge Implicit): 
 > ""…there's a risk of losing some of that knowledge… as folks are approaching that stage of their career.""
 
 ## Balancing Innovation with Risk Management: 
 The tension between rapidly innovating with generative AI and managing the associated risks is another challenge. Addressing this required early establishment of guardrails and involvement of governance-related teams. 
 Relevant Quote: 
 > ""…finding that tension between Innovation and risk management is really important to us.""
 
 # 4. Technical Details
 
 The technical initiatives at Sun Life involve advanced experimentation on legacy mainframe platforms through modern AI technologies:
 
 ## IBM watsonX for Legacy Code Conversion: 
 Sun Life is actively experimenting with IBM's watsonX platform to convert legacy COBOL and PL/I code. This conversion is not a mere translation—it enables the transformation of code into natural language formats. This allows enterprises to preserve and extract historical business logic that might otherwise be lost with retiring coders. 
 Relevant Quote: 
 > ""…we're really excited right now about Watson X and we've been doing some really exciting experimentation on the Mainframe modernization side of things…""
 
 ## Establishment of Enterprise-Ready Generative AI Use Cases: 
 The technical focus also appears to be on ensuring that generative AI applications meet enterprise standards rather than adopting publicly available versions. This requires an emphasis on security, scalability, and integration with existing enterprise systems. 
 Relevant Quote: 
 > ""…having really great Partners like IBM to be a part of the journey and also help understand the Enterprise ready use of generative AI. It's very different than what's publicly available…""
 
 # 5. Results
 
 The results from Sun Life's GenAI adoption efforts, though discussed in a preliminary and experimental phase, indicate promising returns:
 
 ## Increased Operational Efficiency: 
 The modernization of legacy systems via AI-driven code conversion has accelerated client initiatives. This efficiency gain is expected to improve Sun Life's responsiveness and adaptability in a competitive market.
 
 ## Retaining and Extracting Critical Business Knowledge: 
 By converting legacy code into natural language, the company can now extract valuable business rules and processes, ensuring that historical institutional knowledge is preserved and accessible to future developers.
 
 ## Enhanced Employee Competency: 
 The rollout of an enterprise-wide "destination AI" certificate is enhancing overall employee competency in generative AI. This training initiative equips the workforce with the skills to ask critical questions and consciously steer AI outcomes, demonstrating a commitment to ethical and effective AI usage.
 
 Relevant Quote: 
 > ""…the culture piece and the employee training is so important generative AI is everywhere it's in everything that we're doing…""
 
 # 6. Vendors Used
 
 Sun Life's modernization and AI efforts are significantly enhanced through partnerships with leading vendors and institutions:
 
 ## IBM: 
 IBM, with its watsonX platform, plays a key role in driving the technical modernization of Sun Life's mainframe through the conversion of COBOL and PL/I code. This partnership supports both operational efficiency and knowledge preservation. 
 Relevant Quote: 
 > ""…having really great Partners like IBM to be a part of the journey…""
 
 ## CIFAR (Canadian Institute for Advanced Research): 
 CIFAR has partnered with Sun Life to provide advanced AI training programs. Offering the "destination AI" certificate globally to employees underscores the company's commitment to upskilling its workforce in the domain of generative AI. 
 Relevant Quote: 
 > ""…we've recently announced we partnered with CIFAR and we've been able to offer to all Sun Life employees globally the destination AI certificate…""
 
 # Conclusion
 
 Sun Life's strategic approach to adopting generative AI demonstrates how traditional financial institutions can reinvent their legacy systems while empowering employees through AI literacy. Their proactive steps—ranging from technical modernization using IBM's watsonX to establishing strong, ethical guardrails with cross-functional teams—illustrate a comprehensive strategy for driving efficiency and mitigating risks. The vendor partnerships with IBM and CIFAR further underpin their commitment to bridging legacy challenges with cutting-edge technology and workforce advancement.
 
 This case study paints a detailed picture of how modernization, risk management, and employee empowerment can coexist to foster a culture ready for the future of AI in an enterprise setting.`,
    references: [
        {
            id: '1',
            name: 'Strategic AI Adoption: Sun Life',
            author: 'Janet Weldon (Sun Life)',
            year: '2024', // Assuming 2024 based on context
            publisher: 'YouTube',
            url: 'https://www.youtube.com/watch?v=Vj6Ocj36KJs',
        },
    ],
};
